/**
 * caravanaHelper.js
 * La caravana es texto (backend #183): `"101"`, `"AR-7"`, `"52"`.
 * Misma regla de normalización que el backend: recorta, pasa a mayúsculas, saca un
 * `.0` final (Excel) y, si es solo números, los ceros de adelante (`0052` → `52`).
 * Con letras los ceros se conservan (`AR-0052` ≠ `AR-52`).
 */

const SOLO_DIGITOS = /^\d+$/;

/**
 * Normaliza una caravana con la misma regla que el backend.
 * @param {string|number|null|undefined} valor
 * @returns {string|null} la caravana normalizada, o `null` si está vacía.
 */
export function normalizarCaravana(valor) {
  if (valor === null || valor === undefined) return null;
  let s = String(valor).trim().toUpperCase();
  if (s.endsWith(".0")) s = s.slice(0, -2);
  if (s === "") return null;
  if (SOLO_DIGITOS.test(s)) s = s.replace(/^0+(?=\d)/, "");
  return s;
}

/** `true` si las dos caravanas son la misma una vez normalizadas (vacías cuentan iguales). */
export function caravanasIguales(a, b) {
  return normalizarCaravana(a) === normalizarCaravana(b);
}

/**
 * Comparador de orden natural, el mismo que usa el backend: primero las numéricas por
 * valor (9 antes que 10), después las de texto (`localeCompare` numérico) y al final
 * las vacías.
 */
export function compararCaravanas(a, b) {
  const na = normalizarCaravana(a);
  const nb = normalizarCaravana(b);
  if (na === nb) return 0;
  if (na === null) return 1;
  if (nb === null) return -1;
  const numA = SOLO_DIGITOS.test(na);
  const numB = SOLO_DIGITOS.test(nb);
  if (numA && numB) {
    // Comparación por longitud y luego lexicográfica: sin pérdida con caravanas largas.
    return na.length - nb.length || (na < nb ? -1 : 1);
  }
  if (numA) return -1;
  if (numB) return 1;
  return na.localeCompare(nb, "es", { numeric: true });
}

/**
 * Copia de un objeto indexado por caravana con las claves normalizadas (las vacías se
 * descartan). Sirve para buscar `0052` o `ar-1` en un mapa que viene como `52`/`AR-1`.
 * @param {Object<string, *>|null|undefined} mapa
 * @returns {Object<string, *>}
 */
export function normalizarMapaCaravanas(mapa) {
  const resultado = {};
  if (!mapa || typeof mapa !== "object") return resultado;
  for (const [clave, valor] of Object.entries(mapa)) {
    const normalizada = normalizarCaravana(clave);
    if (normalizada !== null) resultado[normalizada] = valor;
  }
  return resultado;
}

/**
 * Búsqueda parcial por caravana: `"ar-1"` encuentra `AR-101` y `"0052"` encuentra `52`.
 * @param {string|number|null|undefined} caravana caravana del animal
 * @param {string|number|null|undefined} busqueda texto ingresado
 * @returns {boolean} `true` si no hay búsqueda o si la caravana la contiene.
 */
export function caravanaCoincide(caravana, busqueda) {
  const q = normalizarCaravana(busqueda);
  if (q === null) return true;
  const c = normalizarCaravana(caravana);
  return c !== null && c.includes(q);
}
